/**
 * Build the WebSocket payload the tablet sends when the YouTube
 * `VideoOverlay` opens or closes (#480).
 *
 * Background: pre-#480 the tablet opened the overlay but never told
 * the server "media is active". The server's idle-timeout therefore
 * used the normal 120 s mid-conversation rung and closed the
 * WebSocket mid-clip — the user pressed Done and found a dead call.
 * Symmetrical fix to #436 for music; video had been missed.
 *
 * Server side (`app/api/voice.py`) the `video_started` payload flips
 * `session_media_active = True` (extending the idle-timeout to
 * `IDLE_TIMEOUT_DURING_MEDIA`, 30 min) and `video_stopped` flips it
 * back to False AND sets `session_media_just_dismissed = "video"` so
 * the LLM's next turn doesn't re-propose another clip out of habit
 * (the #460 anti-priming flag, extended to video here).
 *
 * The function is deliberately tiny and pure: same args → same
 * output, no closures, no I/O. The TabletApp `useEffect` keyed on
 * the boolean `videoOverlayActive` calls it once per transition and
 * sends the result over `wsRef.current`. Keeping the message shape in
 * its own module makes it (a) unit-testable without mounting all of
 * TabletApp.js (3000+ lines) and (b) easy to align with the
 * `music_started`/`music_stopped` shape if we ever consolidate them.
 */

/**
 * @param {boolean} active  true when the video overlay just opened,
 *   false when it just closed.
 * @param {string|null|undefined} title  optional clip title for the
 *   server log line. Passes through verbatim when `active`; ignored
 *   on close (server doesn't need it for `video_stopped`). Null /
 *   undefined → emitted as `null` so the server's
 *   `msg.get('title')` returns a stable falsy value.
 * @returns {{type: 'video_started', title: string|null} | {type: 'video_stopped'}}
 */
export function videoLifecycleSignal(active, title) {
  if (active) {
    return {
      type: 'video_started',
      title: title == null ? null : title,
    };
  }
  return { type: 'video_stopped' };
}
